import { AnaDialogRef, AnaDialogService } from '@anacapri/angular/dialog';
import { Injectable, signal } from '@angular/core';
import { filter, interval, map, switchMap, take, takeWhile, tap } from 'rxjs';

import { AppPropertiesService } from '../services/app-properties/app-properties.service';
import { KeepAliveService } from '../services/keep-alive/keep-alive.service';
import { LogoutService } from '../services/logout/logout.service';
import { SessionExpiringDialogComponent } from './session-expiring/session-expiring-dialog.component';

/**
 * Service for tracking when the user is idle.
 *
 * Once the service starts, the idle countdown will continue until a keep alive request is queued. Keep alive requests
 * use an interval to only send a request at the given interval. So if the interval is 60 seconds, even if the request
 * is queued at second 1, we wait until second 60 before sending the request. No idle time is counted against the user
 * after a request is queued, and it is actually sent. Once the request is sent and the session is refreshed, we
 * restart the idle countdown.
 *
 * Once the limit for the dialog is reached, we show the session expiring dialog. At that point, the only way for the
 * user to extend the current session is to close the dialog.
 *
 *    In order for the idle tracking to work properly, it is best if the following are true:
 *      - to account for latency, SESSION_TIMEOUT is less than the auth app session timeout
 *      - to account for the keep alive interval, either:
 *        - KEEP_ALIVE_INTERVAL + SESSION_TIMEOUT is less than the auth app session timeout OR
 *        - SESSION_TIMEOUT is less than the auth app session timeout and is a multiple of the
 *            KEEP_ALIVE_INTERVAL (e.g. interval: 2min, timeout: 10mins; interval: 3mins timeout: 12mins)
 *
 * @author Jesse Bloomster e020902
 */
@Injectable({
	providedIn: 'root'
})
export class IdleTrackingService {
	private readonly ONE_SECOND_MS = 1000;

	private sessionTimeoutSeconds: number;
	private keepAliveIntervalSeconds: number;
	private sessionDialogLimitSeconds: number;

	private serviceRunningInd = false;
	private keepAliveRequestQueued = false;
	private sessionExpiringDialogInd = false;

	private sessionExpiringDialogRef: AnaDialogRef;

	idleSecondsRemainingSig = signal<number>(0);

	/**
	 * @param appPropertiesService the global app properties service
	 * @param keepAliveService the service to send keepAlive requests
	 * @param dialogService the Anacapri dialog service
	 * @param logoutService the logout service
	 */
	constructor(
		appPropertiesService: AppPropertiesService,
		private keepAliveService: KeepAliveService,
		private dialogService: AnaDialogService,
		private logoutService: LogoutService
	) {
		this.sessionTimeoutSeconds = appPropertiesService.appProperties.sessionTimeoutSeconds;
		this.keepAliveIntervalSeconds = appPropertiesService.appProperties.keepAliveIntervalSeconds;
		this.sessionDialogLimitSeconds = appPropertiesService.appProperties.sessionDialogLimitSeconds;
	}

	/**
	 * Return if the idle service is currently running.
	 */
	isServiceRunning = () => this.serviceRunningInd;

	/**
	 * Return if the session expiring dialog is open.
	 */
	isSessionExpiringDialogOpen = () => this.sessionExpiringDialogInd;

	/**
	 * Start the idle service.
	 */
	startService() {
		this.serviceRunningInd = true;
		this.startIdleCountdown();
		this.startKeepAliveRequests();
	}

	/**
	 * Stop the idle service and clean up all subscriptions.
	 */
	stopService() {
		this.serviceRunningInd = false;
	}

	/**
	 * Reset the idle countdown.
	 */
	resetIdleCountdown() {
		this.keepAliveRequestQueued = true;
	}

	/**
	 * Start the interval of keep alive requests to signal to the server that the user is still active. If a keep alive
	 * request has been queued, once the interval is hit, send the request. If the request is successful, restart the
	 * idle countdown. If the request is unsuccessful, logout the user and route them to the track-a-claim page.
	 * If no keep alive has been queued, do nothing until next interval.
	 */
	private startKeepAliveRequests() {
		interval(this.keepAliveIntervalSeconds * this.ONE_SECOND_MS)
			.pipe(
				takeWhile(() => this.serviceRunningInd),
				filter(() => this.keepAliveRequestQueued),
				switchMap(() =>
					this.keepAliveService.keepAlive().pipe(
						tap(sessionValid => {
							// if the session is valid, restart idle countdown; otherwise, logout user
							if (sessionValid) {
								this.keepAliveRequestQueued = false;
								this.startIdleCountdown();
							} else {
								this.stopService();
								this.logoutService.logout();
							}
						})
					)
				)
			)
			.subscribe();
	}

	/**
	 * Start the idle countdown. The idle countdown continues to count down as long as the idle service is running
	 * and no keep alive request has been queued. If the seconds remaining should show the session expiring dialog, open
	 * the dialog. If the session timeout number of seconds has been reached, we stop the countdown with a timeout error.
	 */
	private startIdleCountdown() {
		interval(this.ONE_SECOND_MS)
			.pipe(
				takeWhile(() => this.serviceRunningInd && !this.keepAliveRequestQueued),
				map(i => {
					const secondsRemaining = this.sessionTimeoutSeconds - i;

					if (secondsRemaining > 0) {
						if (secondsRemaining === this.sessionDialogLimitSeconds) {
							this.showSessionExpiringDialog();
						}

						return secondsRemaining;
					} else {
						throw new Error('Timeout');
					}
				})
			)
			.subscribe({
				next: secondsRemaining => this.idleSecondsRemainingSig.set(secondsRemaining),
				error: () => {
					this.stopService();
					this.logoutService.logout();
				}
			});
	}

	/**
	 * Show the session expiring dialog. If the dialog is closed before the current session is expired, we queue
	 * up a keep alive request.
	 */
	private showSessionExpiringDialog() {
		this.sessionExpiringDialogInd = true;

		this.sessionExpiringDialogRef = this.dialogService.open(SessionExpiringDialogComponent, {
			heading: 'Session Expiring',
			classes: 'large-6 medium-7 small-12'
		});

		// if the service is still running and the dialog is closed, reset the countdown
		this.sessionExpiringDialogRef.afterClosed.pipe(take(1)).subscribe(() => {
			this.sessionExpiringDialogInd = false;

			if (this.serviceRunningInd) {
				this.resetIdleCountdown();
			}
		});
	}
}
